import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { FileText, Upload, List, Plus, Trash2, Loader2, Paperclip, X, File, FolderOpen, Lock, Users } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateTest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("manual");
  const fileInputRefs = useRef({});

  // Form state
  const [testName, setTestName] = useState("");
  const [testDescription, setTestDescription] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [browser, setBrowser] = useState("chromium");
  const [steps, setSteps] = useState([{ step_number: 1, description: "", expected_result: "", attachments: [] }]);
  
  // Suite selection state
  const [suites, setSuites] = useState([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState("");
  const [showCreateSuiteModal, setShowCreateSuiteModal] = useState(false);
  const [newSuiteName, setNewSuiteName] = useState("");
  const [newSuiteDescription, setNewSuiteDescription] = useState("");
  const [creatingSuite, setCreatingSuite] = useState(false);
  
  // Tags state
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  
  // Privacy / sharing state
  const [isSharedWithTeam, setIsSharedWithTeam] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  
  // Text input state
  const [textInput, setTextInput] = useState("");

  // File upload state for bulk import
  const [uploadedFile, setUploadedFile] = useState(null);

  // Fetch suites on mount
  useEffect(() => {
    fetchSuites();
    fetchUserTeams();
  }, []);

  const fetchSuites = async () => {
    try {
      const response = await axios.get(`${API}/suites`, { withCredentials: true });
      setSuites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch suites:', error);
    }
  };

  const fetchUserTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setUserTeams(response.data?.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const handleCreateSuite = async () => {
    if (!newSuiteName.trim()) {
      toast.error('Please enter a suite name');
      return;
    }
    
    setCreatingSuite(true);
    try {
      const response = await axios.post(`${API}/suites`, {
        name: newSuiteName,
        description: newSuiteDescription
      }, { withCredentials: true });
      
      toast.success('Suite created!');
      setShowCreateSuiteModal(false);
      setNewSuiteName('');
      setNewSuiteDescription('');
      
      // Refresh suites and select the new one
      await fetchSuites();
      setSelectedSuiteId(response.data.id || response.data.suite_id);
    } catch (error) {
      console.error('Failed to create suite:', error);
      toast.error('Failed to create suite');
    } finally {
      setCreatingSuite(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, { step_number: steps.length + 1, description: "", expected_result: "", attachments: [] }]);
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    const renumbered = newSteps.map((step, i) => ({ ...step, step_number: i + 1 }));
    setSteps(renumbered);
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index][field] = value;
    setSteps(newSteps);
  };

  // Handle file attachment for a specific step
  const handleStepFileUpload = async (stepIndex, files) => {
    if (!files || files.length === 0) return;

    const newAttachments = [];
    
    for (const file of files) {
      // Check file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload-step-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
        
        newAttachments.push(response.data);
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(`Failed to upload "${file.name}"`);
      }
    }

    if (newAttachments.length > 0) {
      const newSteps = [...steps];
      newSteps[stepIndex].attachments = [
        ...(newSteps[stepIndex].attachments || []),
        ...newAttachments
      ];
      setSteps(newSteps);
      toast.success(`${newAttachments.length} file(s) attached`);
    }
  };

  const removeAttachment = (stepIndex, attachmentIndex) => {
    const newSteps = [...steps];
    newSteps[stepIndex].attachments.splice(attachmentIndex, 1);
    setSteps(newSteps);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return '🖼️';
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('json')) return '📋';
    return '📎';
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!testName || steps.some(s => !s.description)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/test-cases`, {
        name: testName,
        description: testDescription,
        target_url: targetUrl,
        browser: browser,
        steps: steps,
        tags: tags,
        is_shared_with_team: isSharedWithTeam,
        suite_id: selectedSuiteId || null
      }, { withCredentials: true });
      
      // If suite selected, also add test case to suite
      if (selectedSuiteId && response.data.id) {
        try {
          await axios.post(`${API}/suites/${selectedSuiteId}/test-cases`, {
            test_case_id: response.data.id
          }, { withCredentials: true });
        } catch (suiteError) {
          console.error('Failed to add test to suite:', suiteError);
          // Don't block creation if suite addition fails
        }
      }
      
      toast.success("Test case created successfully!");
      navigate(`/dashboard`);
    } catch (error) {
      console.error('Failed to create test case:', error);
      toast.error("Failed to create test case");
    } finally {
      setLoading(false);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!textInput.trim()) {
      toast.error("Please enter test case description");
      return;
    }

    setLoading(true);
    try {
      const parsedData = await axios.post(`${API}/test-cases/parse-text`, { text: textInput }, { withCredentials: true });
      const response = await axios.post(`${API}/test-cases`, parsedData.data, { withCredentials: true });
      toast.success("Test case created from text!");
      navigate(`/dashboard`);
    } catch (error) {
      console.error('Failed to parse and create test case:', error);
      toast.error("Failed to parse test case. Please check your input.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadedFile) {
      toast.error("Please upload a file");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      const parsedData = await axios.post(`${API}/test-cases/parse-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      for (const testCase of parsedData.data.test_cases) {
        await axios.post(`${API}/test-cases`, testCase, { withCredentials: true });
      }

      toast.success(`${parsedData.data.test_cases.length} test case(s) created from file!`);
      navigate(`/dashboard`);
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast.error("Failed to parse file. Please check the format.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="create-test-page">
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        <AppNav />

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Create Test Case
              </h1>
              <p className="text-xl text-slate-400 mb-12 max-w-2xl">
                Define your test steps manually or let AI parse them from natural language
              </p>

              <Card className="glass-card border-white/5 rounded-xl">
                <CardContent className="p-8">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full grid grid-cols-3 bg-[#0B0F1A] mb-8">
                      <TabsTrigger value="manual" data-testid="tab-manual" className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black">
                        <List className="w-4 h-4 mr-2" />
                        Manual Entry
                      </TabsTrigger>
                      <TabsTrigger value="text" data-testid="tab-text" className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black">
                        <FileText className="w-4 h-4 mr-2" />
                        From Text
                      </TabsTrigger>
                      <TabsTrigger value="upload" data-testid="tab-upload" className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload File
                      </TabsTrigger>
                    </TabsList>

                    {/* Manual Entry Tab */}
                    <TabsContent value="manual">
                      <form onSubmit={handleManualSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="test-name" className="text-slate-300 mb-2 block">Test Name *</Label>
                            <Input
                              id="test-name"
                              data-testid="test-name-input"
                              placeholder="Login Test"
                              className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                              value={testName}
                              onChange={(e) => setTestName(e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="target-url" className="text-slate-300 mb-2 block">Target URL</Label>
                            <Input
                              id="target-url"
                              data-testid="target-url-input"
                              placeholder="https://example.com"
                              className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                              value={targetUrl}
                              onChange={(e) => setTargetUrl(e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="test-description" className="text-slate-300 mb-2 block">Description</Label>
                          <Textarea
                            id="test-description"
                            data-testid="test-description-input"
                            placeholder="Describe what this test validates..."
                            className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                            value={testDescription}
                            onChange={(e) => setTestDescription(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label className="text-slate-300 mb-2 block">Browser</Label>
                          <Select value={browser} onValueChange={setBrowser}>
                            <SelectTrigger data-testid="browser-select" className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0B0F1A] border-white/10">
                              <SelectItem value="chromium">Chromium</SelectItem>
                              <SelectItem value="firefox">Firefox</SelectItem>
                              <SelectItem value="webkit">WebKit (Safari)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Suite Selection */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-slate-300">Add to Collection/Suite</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-[#D946EF] hover:text-[#C026D3] text-xs"
                              onClick={() => setShowCreateSuiteModal(true)}
                              data-testid="create-suite-btn"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              New Suite
                            </Button>
                          </div>
                          <Select value={selectedSuiteId || "none"} onValueChange={(val) => setSelectedSuiteId(val === "none" ? "" : val)}>
                            <SelectTrigger data-testid="suite-select" className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm">
                              <SelectValue placeholder="Select a suite (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0B0F1A] border-white/10">
                              <SelectItem value="none">No Suite (optional)</SelectItem>
                              {suites.map((suite) => (
                                <SelectItem key={suite.id || suite.suite_id} value={suite.id || suite.suite_id}>
                                  <div className="flex items-center gap-2">
                                    <FolderOpen className="w-4 h-4 text-[#F59E0B]" />
                                    {suite.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500 mt-1">
                            Add this test to a collection for organized regression testing
                          </p>
                        </div>

                        {/* Tags Input */}
                        <div>
                          <Label className="text-slate-300 mb-2 block">Tags</Label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {tags.map((tag, index) => (
                              <Badge
                                key={index}
                                className="bg-[#D946EF]/20 text-[#D946EF] border-[#D946EF]/30 flex items-center gap-1"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => setTags(tags.filter((_, i) => i !== index))}
                                  className="ml-1 hover:text-white"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              data-testid="tag-input"
                              placeholder="Add a tag (press Enter)"
                              className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm flex-1"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && tagInput.trim()) {
                                  e.preventDefault();
                                  if (!tags.includes(tagInput.trim())) {
                                    setTags([...tags, tagInput.trim()]);
                                  }
                                  setTagInput('');
                                }
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-white/10 text-slate-300 hover:text-white"
                              onClick={() => {
                                if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                                  setTags([...tags, tagInput.trim()]);
                                  setTagInput('');
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Tags help organize and filter test cases (e.g., regression, smoke, login)
                          </p>
                        </div>

                        {/* Privacy / Share with Team */}
                        {userTeams.length > 0 && (
                          <div data-testid="privacy-toggle-wrapper" className="p-4 bg-black/30 border border-white/10 rounded-lg">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                {isSharedWithTeam ? (
                                  <Users className="w-5 h-5 text-[#00E599] mt-0.5" />
                                ) : (
                                  <Lock className="w-5 h-5 text-slate-400 mt-0.5" />
                                )}
                                <div>
                                  <Label className="text-slate-200 font-medium">
                                    {isSharedWithTeam ? 'Shared with Team' : 'Private'}
                                  </Label>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {isSharedWithTeam
                                      ? 'Visible to all your team members on the Shared Dashboard'
                                      : 'Only visible to you (and your team owner/leader)'}
                                  </p>
                                </div>
                              </div>
                              <Switch
                                data-testid="share-with-team-toggle"
                                checked={isSharedWithTeam}
                                onCheckedChange={setIsSharedWithTeam}
                              />
                            </div>
                          </div>
                        )}

                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <Label className="text-slate-300">Test Steps *</Label>
                            <Button 
                              data-testid="add-step-btn"
                              type="button"
                              size="sm"
                              className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm"
                              onClick={addStep}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Step
                            </Button>
                          </div>

                          <div className="space-y-4">
                            {steps.map((step, index) => (
                              <div key={index} className="glass-card p-4 rounded-sm">
                                <div className="flex items-start gap-4">
                                  <div className="w-8 h-8 bg-[#00E599]/10 rounded-sm flex items-center justify-center flex-shrink-0 mt-1">
                                    <span className="text-[#00E599] font-bold">{step.step_number}</span>
                                  </div>
                                  <div className="flex-1 space-y-3">
                                    <Input
                                      data-testid={`step-description-${index}`}
                                      placeholder="Step description (e.g., Click login button)"
                                      className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                                      value={step.description}
                                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                                      required
                                    />
                                    <Input
                                      data-testid={`step-expected-${index}`}
                                      placeholder="Expected result — e.g.: 'Welcome' text appears, URL contains /dashboard, error message is not visible"
                                      className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm placeholder:text-slate-600"
                                      value={step.expected_result}
                                      onChange={(e) => updateStep(index, 'expected_result', e.target.value)}
                                    />
                                    
                                    {/* File Attachments Section */}
                                    <div className="pt-2 border-t border-white/5">
                                      <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs text-slate-500 flex items-center gap-1">
                                          <Paperclip className="w-3 h-3" />
                                          Attachments (for file upload actions)
                                        </Label>
                                        <div>
                                          <input
                                            type="file"
                                            multiple
                                            className="hidden"
                                            ref={el => fileInputRefs.current[index] = el}
                                            onChange={(e) => handleStepFileUpload(index, e.target.files)}
                                          />
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="text-[#00E599] hover:text-[#00CC88] text-xs h-7"
                                            onClick={() => fileInputRefs.current[index]?.click()}
                                            data-testid={`attach-file-btn-${index}`}
                                          >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Attach File
                                          </Button>
                                        </div>
                                      </div>
                                      
                                      {/* Display attached files */}
                                      {step.attachments && step.attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                          {step.attachments.map((attachment, attIndex) => (
                                            <Badge
                                              key={attIndex}
                                              className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 pr-1 flex items-center gap-1"
                                            >
                                              <span>{getFileIcon(attachment.content_type)}</span>
                                              <span className="max-w-[120px] truncate">{attachment.filename}</span>
                                              <span className="text-[10px] text-slate-400">({formatFileSize(attachment.size)})</span>
                                              <button
                                                type="button"
                                                onClick={() => removeAttachment(index, attIndex)}
                                                className="ml-1 hover:bg-[#D946EF]/20 rounded p-0.5"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {steps.length > 1 && (
                                    <Button
                                      data-testid={`remove-step-btn-${index}`}
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm flex-shrink-0"
                                      onClick={() => removeStep(index)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add Step at Bottom */}
                          {steps.length > 0 && (
                            <div className="mt-4 flex justify-center">
                              <Button 
                                data-testid="add-step-bottom-btn"
                                type="button"
                                variant="outline"
                                className="border-dashed border-white/20 text-slate-400 hover:text-white hover:border-[#00E599] hover:bg-[#00E599]/5 rounded-sm"
                                onClick={addStep}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Step Below
                              </Button>
                            </div>
                          )}
                        </div>

                        <Button 
                          data-testid="submit-manual-btn"
                          type="submit" 
                          className="w-full bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            'Create Test Case'
                          )}
                        </Button>
                      </form>
                    </TabsContent>

                    {/* Text Input Tab */}
                    <TabsContent value="text">
                      <form onSubmit={handleTextSubmit} className="space-y-6">
                        <div>
                          <Label htmlFor="text-input" className="text-slate-300 mb-2 block">Describe your test case</Label>
                          <Textarea
                            id="text-input"
                            data-testid="text-input"
                            placeholder={`Example:
Test Name: User Login Flow
Description: Verify that users can log in with valid credentials

Steps:
1. Navigate to the login page
2. Enter username "testuser@example.com"
3. Enter password "password123"
4. Click the Login button
5. Verify that the dashboard is displayed`}
                            className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm min-h-[300px] code-font"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                          />
                          <p className="text-sm text-slate-500 mt-2">AI will parse this into structured test steps using Gemini 3 Pro</p>
                        </div>
                        <Button 
                          data-testid="submit-text-btn"
                          type="submit" 
                          className="w-full bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Parsing with AI...
                            </>
                          ) : (
                            'Create Test Case'
                          )}
                        </Button>
                      </form>
                    </TabsContent>

                    {/* File Upload Tab */}
                    <TabsContent value="upload">
                      <form onSubmit={handleFileUpload} className="space-y-6">
                        <div>
                          <Label htmlFor="file-upload" className="text-slate-300 mb-2 block">Upload test case file</Label>
                          <div className="border-2 border-dashed border-white/10 rounded-sm p-8 text-center hover:border-[#00E599]/50 transition-colors">
                            <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                            <Input
                              id="file-upload"
                              data-testid="file-upload-input"
                              type="file"
                              accept=".csv,.json"
                              className="hidden"
                              onChange={(e) => setUploadedFile(e.target.files[0])}
                            />
                            <Label htmlFor="file-upload" className="cursor-pointer">
                              <span className="text-[#00E599] hover:underline">Click to upload</span>
                              <span className="text-slate-400"> or drag and drop</span>
                            </Label>
                            <p className="text-sm text-slate-500 mt-2">CSV or JSON files only</p>
                            {uploadedFile && (
                              <p className="text-sm text-[#00E599] mt-4">Selected: {uploadedFile.name}</p>
                            )}
                          </div>
                        </div>
                        <Button 
                          data-testid="submit-file-btn"
                          type="submit" 
                          className="w-full bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                          disabled={loading || !uploadedFile}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Create from File'
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Create Suite Modal */}
      <Dialog open={showCreateSuiteModal} onOpenChange={setShowCreateSuiteModal}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <FolderOpen className="w-5 h-5 text-[#F59E0B]" />
              Create New Suite
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a collection to organize related test cases
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Suite Name *</Label>
              <Input
                data-testid="new-suite-name-input"
                placeholder="e.g., Regression Tests, Smoke Tests"
                className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                value={newSuiteName}
                onChange={(e) => setNewSuiteName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Description</Label>
              <Textarea
                data-testid="new-suite-description-input"
                placeholder="Describe this test suite..."
                className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                value={newSuiteDescription}
                onChange={(e) => setNewSuiteDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => setShowCreateSuiteModal(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="create-suite-submit-btn"
              className="bg-[#F59E0B] text-black hover:bg-[#D97706]"
              onClick={handleCreateSuite}
              disabled={creatingSuite || !newSuiteName.trim()}
            >
              {creatingSuite ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Create Suite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
